import { useState, useEffect } from "react";
import { useQuery } from "react-query";
import {
  Button,
  Col,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "Components/Core";
import { CoachPromotionalOffer } from "Interfaces/Components/Shared/AssetVideoDetailInterface";
import {
  createCoachPromotionalOffer,
  updateCoachPromotionalOffer,
  CoachPromotionalOfferPayload,
} from "Api/Pages/CoachPromotionalOffersApi";
import { getCoachList } from "Api/Pages/CoachesApi";
import USE_QUERY_KEYS_CONSTANTS from "Api/useQueryKeyConstants";
import { setToastMessage } from "App/commonReducer";
import { useAppDispatch } from "App/hooks";
import { SUCCESS } from "App/constants";

interface CoachOption {
  id: number;
  name: string;
  video_review_rates: { INR: number; USD: number };
}

interface CoachPromotionalOfferModalProps {
  isVisible: boolean;
  assetId: number;
  existingOffer: CoachPromotionalOffer | null;
  onClose: () => void;
  onSuccess: () => void;
}

const BLANK_RATES = { INR: 0, USD: 0 };

const CoachPromotionalOfferModal: React.FC<CoachPromotionalOfferModalProps> = ({
  isVisible,
  assetId,
  existingOffer,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const isEditMode = existingOffer !== null;

  const [coachId, setCoachId] = useState<number | "">("");
  const [reviewRates, setReviewRates] = useState(BLANK_RATES);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: coachesData } = useQuery(
    USE_QUERY_KEYS_CONSTANTS.coachesList,
    () =>
      getCoachList({
        academyId: 0,
        searchInput: "",
        selectValue: "",
        currentPage: 1,
      }),
    { enabled: isVisible, refetchOnWindowFocus: false }
  );

  const coaches: CoachOption[] = (coachesData?.coaches || []).map(
    (c: {
      id: number;
      name: string;
      video_review_rates: { INR: number; USD: number };
    }) => ({
      id: c.id,
      name: c.name,
      video_review_rates: c.video_review_rates || { INR: 0, USD: 0 },
    })
  );

  useEffect(() => {
    if (!isVisible) return;

    if (isEditMode && existingOffer) {
      setCoachId(existingOffer.coach_id);
      setReviewRates({
        INR: existingOffer.review_rates.INR,
        USD: existingOffer.review_rates.USD,
      });
    } else {
      setCoachId("");
      setReviewRates(BLANK_RATES);
    }
  }, [isVisible, isEditMode, existingOffer]);

  const handleRateChange = (currency: "INR" | "USD", value: string) => {
    setReviewRates((prev) => ({
      ...prev,
      [currency]: Number(value) || 0,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!coachId) return;

    const payload: CoachPromotionalOfferPayload = {
      coach_id: coachId as number,
      video_count: 3,
      review_rates: reviewRates,
    };

    setIsSubmitting(true);
    try {
      if (isEditMode && existingOffer) {
        await updateCoachPromotionalOffer(assetId, existingOffer.id, payload);
        dispatch(
          setToastMessage([["Promotional offer updated successfully"], SUCCESS])
        );
      } else {
        await createCoachPromotionalOffer(assetId, payload);
        dispatch(
          setToastMessage([["Promotional offer created successfully"], SUCCESS])
        );
      }
      onSuccess();
      onClose();
    } catch {
      // errors handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCoach = coaches.find((c) => c.id === coachId);

  const isSubmitDisabled =
    isSubmitting || !coachId || reviewRates.INR <= 0 || reviewRates.USD <= 0;

  return (
    <Modal centered size="lg" show={isVisible} onHide={onClose}>
      <ModalHeader>
        {isEditMode ? "Edit Promotional Offer" : "Add Promotional Offer"}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Row className="g-3">
            <Col sm={12}>
              <Form.Group controlId="coach">
                <Form.Label className="fs-xs">Coach *</Form.Label>
                <Form.Select
                  className="ss-select-control bg-white"
                  value={coachId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setCoachId(Number(e.target.value) || "")
                  }
                  disabled={isEditMode}
                  required
                >
                  <option value="" disabled>
                    Select Coach
                  </option>
                  {coaches.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {selectedCoach && (
              <Col sm={12}>
                <Form.Group controlId="perVideoRate">
                  <Form.Label className="fs-xs">Per-video Rate</Form.Label>
                  <Form.Control
                    type="text"
                    className="bg-light"
                    value={`₹${selectedCoach.video_review_rates.INR} / $${selectedCoach.video_review_rates.USD}`}
                    disabled
                  />
                </Form.Group>
              </Col>
            )}

            <Col sm={12} md={4}>
              <Form.Group controlId="videoCount">
                <Form.Label className="fs-xs">Bundle Size</Form.Label>
                <Form.Control
                  type="text"
                  className="bg-light"
                  value="3 Videos"
                  disabled
                />
              </Form.Group>
            </Col>

            <Col sm={12} md={4}>
              <Form.Group controlId="rateINR">
                <Form.Label className="fs-xs">Bundle Rate (INR) *</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={1}
                  className="bg-white"
                  placeholder="e.g. 8000"
                  value={reviewRates.INR || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleRateChange("INR", e.target.value)
                  }
                  required
                />
              </Form.Group>
            </Col>

            <Col sm={12} md={4}>
              <Form.Group controlId="rateUSD">
                <Form.Label className="fs-xs">Bundle Rate (USD) *</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={0.01}
                  className="bg-white"
                  placeholder="e.g. 100"
                  value={reviewRates.USD || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleRateChange("USD", e.target.value)
                  }
                  required
                />
              </Form.Group>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline-secondary"
            size="sm"
            className="px-4"
            onClick={onClose}
            disabled={isSubmitting}
            type="button"
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="px-4"
            type="submit"
            disabled={isSubmitDisabled}
          >
            {isEditMode ? "Save" : "Add"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CoachPromotionalOfferModal;
